import type {
  ArenaConfig,
  FoodConfig,
  FoodId,
  FoodPopulationConfig,
} from "../config/gameplay-config";
import type { RandomSource } from "./seeded-random";

export interface FoodPosition {
  readonly x: number;
  readonly y: number;
}

export interface FoodEntity {
  readonly id: string;
  readonly position: FoodPosition;
  readonly radius: number;
  readonly variantId: FoodId;
}

export interface FoodExclusionZone {
  readonly position: FoodPosition;
  readonly radius: number;
}

export interface FoodPopulationOptions {
  readonly arena: ArenaConfig;
  readonly config: FoodPopulationConfig;
  readonly initialExclusions: readonly FoodExclusionZone[];
  readonly random: RandomSource;
  readonly variants: readonly FoodConfig[];
}

export interface FoodClusterDistance {
  readonly maximum: number;
  readonly minimum: number;
}

export interface FoodClusterPlan {
  readonly foods: readonly FoodEntity[];
  readonly nextIdentifier: number;
  readonly revision: number;
}

type PositionFactory = (radius: number) => FoodPosition;

const squaredDistance = (left: FoodPosition, right: FoodPosition): number => {
  const x = left.x - right.x;
  const y = left.y - right.y;
  return x * x + y * y;
};

const isOutsideZone = (
  position: FoodPosition,
  radius: number,
  zone: FoodExclusionZone,
): boolean =>
  squaredDistance(position, zone.position) >= (radius + zone.radius) ** 2;

export class FoodPopulation {
  private readonly foods: FoodEntity[] = [];
  private nextIdentifier = 1;
  private revision = 0;

  public constructor(private readonly options: FoodPopulationOptions) {
    this.fillToTarget(options.initialExclusions);
  }

  public getSnapshot(): readonly FoodEntity[] {
    return this.foods.map((food) => ({
      ...food,
      position: { ...food.position },
    }));
  }

  public remove(
    identifier: string,
    exclusions: readonly FoodExclusionZone[] = [],
  ): boolean {
    const index = this.foods.findIndex((food) => food.id === identifier);
    if (index < 0) {
      return false;
    }
    this.foods.splice(index, 1);
    this.revision += 1;
    this.fillToTarget(exclusions);
    return true;
  }

  public commitCluster(plan: FoodClusterPlan): void {
    if (
      plan.revision !== this.revision ||
      plan.nextIdentifier !== this.nextIdentifier
    ) {
      throw new Error("Food cluster plan is stale");
    }
    if (plan.foods.length === 0) {
      return;
    }
    this.foods.push(
      ...plan.foods.map((food) => ({
        ...food,
        position: { ...food.position },
      })),
    );
    this.nextIdentifier += plan.foods.length;
    this.revision += 1;
  }

  public planCluster(
    center: FoodPosition,
    count: number,
    distance: FoodClusterDistance,
    random: RandomSource,
    exclusions: readonly FoodExclusionZone[] = [],
  ): FoodClusterPlan | null {
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new RangeError("Food cluster count must be a non-negative integer");
    }
    if (
      !Number.isFinite(center.x) ||
      !Number.isFinite(center.y) ||
      !Number.isFinite(distance.minimum) ||
      !Number.isFinite(distance.maximum) ||
      distance.minimum < 0 ||
      distance.maximum <= distance.minimum
    ) {
      return null;
    }
    const requested = Math.min(
      count,
      this.options.config.maximum - this.foods.length,
    );
    const planned: FoodEntity[] = [];
    while (planned.length < requested) {
      const added = this.planFood(
        center,
        distance,
        random,
        planned,
        this.nextIdentifier + planned.length,
        exclusions,
      );
      if (added === null) {
        return null;
      }
      planned.push(added);
    }
    return {
      foods: planned,
      nextIdentifier: this.nextIdentifier,
      revision: this.revision,
    };
  }

  public spawnNear(position: FoodPosition, count: number): number {
    if (!Number.isSafeInteger(count) || count < 0) {
      throw new RangeError("Food spawn count must be a non-negative integer");
    }
    const available = this.options.config.maximum - this.foods.length;
    const requested = Math.min(count, available);
    const exclusions = [
      {
        position,
        radius: this.options.config.debugNearMinimumDistance,
      },
    ];
    let spawned = 0;
    while (spawned < requested) {
      const added = this.trySpawn(
        (radius) => this.createNearCandidate(position, radius),
        exclusions,
      );
      if (!added) {
        break;
      }
      spawned += 1;
    }
    return spawned;
  }

  private createArenaCandidate(radius: number): FoodPosition {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    return {
      x:
        inset +
        this.options.random.nextUnit() * (this.options.arena.width - inset * 2),
      y:
        inset +
        this.options.random.nextUnit() *
          (this.options.arena.height - inset * 2),
    };
  }

  private createNearCandidate(
    center: FoodPosition,
    _radius: number,
  ): FoodPosition {
    void _radius;
    const angle = this.options.random.nextUnit() * Math.PI * 2;
    const distance =
      this.options.config.debugNearMinimumDistance +
      this.options.random.nextUnit() *
        (this.options.config.debugNearMaximumDistance -
          this.options.config.debugNearMinimumDistance);
    return {
      x: center.x + Math.cos(angle) * distance,
      y: center.y + Math.sin(angle) * distance,
    };
  }

  private createClusterCandidate(
    center: FoodPosition,
    distance: FoodClusterDistance,
    random: RandomSource,
  ): FoodPosition {
    const angle = random.nextUnit() * Math.PI * 2;
    const radius =
      distance.minimum +
      random.nextUnit() * (distance.maximum - distance.minimum);
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  }

  private fillToTarget(exclusions: readonly FoodExclusionZone[]): void {
    while (this.foods.length < this.options.config.target) {
      if (
        !this.trySpawn(
          (radius) => this.createArenaCandidate(radius),
          exclusions,
        )
      ) {
        break;
      }
    }
    if (this.foods.length < this.options.config.minimum) {
      throw new Error("Food population could not reach its configured minimum");
    }
  }

  private isValidPosition(
    position: FoodPosition,
    radius: number,
    exclusions: readonly FoodExclusionZone[],
    planned: readonly FoodEntity[] = [],
  ): boolean {
    const inset =
      this.options.arena.boundaryInset +
      this.options.config.spawnInset +
      radius;
    if (
      position.x < inset ||
      position.x > this.options.arena.width - inset ||
      position.y < inset ||
      position.y > this.options.arena.height - inset
    ) {
      return false;
    }
    if (!exclusions.every((zone) => isOutsideZone(position, radius, zone))) {
      return false;
    }
    return [...this.foods, ...planned].every((food) =>
      isOutsideZone(position, radius, {
        position: food.position,
        radius: food.radius + this.options.config.minimumFoodSpacing,
      }),
    );
  }

  private planFood(
    center: FoodPosition,
    distance: FoodClusterDistance,
    random: RandomSource,
    planned: readonly FoodEntity[],
    identifier: number,
    exclusions: readonly FoodExclusionZone[],
  ): FoodEntity | null {
    for (
      let attempt = 0;
      attempt < this.options.config.maximumSpawnAttemptsPerFood;
      attempt += 1
    ) {
      const variant =
        this.options.variants[
          random.nextInteger(0, this.options.variants.length)
        ];
      if (variant === undefined) {
        throw new Error("Food variants must not be empty");
      }
      const position = this.createClusterCandidate(center, distance, random);
      if (
        !this.isValidPosition(position, variant.radius, exclusions, planned)
      ) {
        continue;
      }
      return {
        id: `food-${String(identifier).padStart(4, "0")}`,
        position,
        radius: variant.radius,
        variantId: variant.id,
      };
    }
    return null;
  }

  private trySpawn(
    createPosition: PositionFactory,
    exclusions: readonly FoodExclusionZone[],
  ): boolean {
    for (
      let attempt = 0;
      attempt < this.options.config.maximumSpawnAttemptsPerFood;
      attempt += 1
    ) {
      const variant =
        this.options.variants[
          this.options.random.nextInteger(0, this.options.variants.length)
        ];
      if (variant === undefined) {
        throw new Error("Food variants must not be empty");
      }
      const position = createPosition(variant.radius);
      if (!this.isValidPosition(position, variant.radius, exclusions)) {
        continue;
      }
      const id = `food-${String(this.nextIdentifier).padStart(4, "0")}`;
      this.nextIdentifier += 1;
      this.foods.push({
        id,
        position,
        radius: variant.radius,
        variantId: variant.id,
      });
      this.revision += 1;
      return true;
    }
    return false;
  }
}
